import Head from 'next/head';
import { HrefLangMap, LocaleEnum } from 'schema/locale';
import { PageTranslation } from 'schema/studio/studio';
import { getCanonicalLink } from 'shared/utils/getCanonicalLink';

import type { JSX } from 'react';

type Props = {
  translations?: (PageTranslation | null | undefined)[];
};

/**
 * Our locale enum targets language and shopify store. We need to convert the
 * store (EU/UK/etc) to the countries that are targeted with that store.
 */
const getLinkDataForLocale = ({ language: locale, slug }: PageTranslation) => {
  if (!(locale in HrefLangMap)) {
    return null;
  }
  return HrefLangMap[locale].map((lang) => ({ lang, locale, slug: slug.current }));
};

const getLinksForTranslations = (translations: PageTranslation[]): JSX.Element[] => {
  return translations
    .flatMap((translation) => getLinkDataForLocale(translation))
    .filter((i) => i !== null)
    .map(({ lang, locale, slug }) => (
      <link key={lang} rel="alternate" hrefLang={lang} href={getCanonicalLink(slug, locale)} />
    ));
};

export const AlternateLinks = ({ translations: initTranslations }: Props) => {
  const translations = (initTranslations || []).filter((i) => i !== null && i !== undefined);

  const getHreflangLinks = () => {
    const links: JSX.Element[] = getLinksForTranslations(translations);

    // We currenly only choose english for x-default if there is one.
    const englishTranslation = translations.find(({ language }) => language === LocaleEnum.enUS);
    if (englishTranslation) {
      links.push(
        <link
          key="x-default"
          rel="alternate"
          hrefLang="x-default"
          href={getCanonicalLink(englishTranslation.slug.current, LocaleEnum.enUS)}
        />
      );
    }

    return links;
  };

  return <Head>{getHreflangLinks()}</Head>;
};
